import React, { Component } from 'react';
import PropTypes from 'prop-types';

import AuthService from '../../services/AuthService';
import TranslatedFilePond from '../TranslatedFilePond';

class FileUpload extends Component {
  getServerOptions() {
    const { endpoint, relationId } = this.props;
    let token = AuthService.getToken();

    const doProcess = (
      fieldName,
      file,
      metadata,
      load,
      error,
      progress,
      abort,
      retryOnUnauthorized,
    ) => {
      // fieldName is the name of the input field
      // file is the actual file object to send
      const formData = new FormData();

      formData.append(fieldName, file, file.name);
      if (relationId) {
        formData.append('relationId', relationId);
      }
      const url = new URL(endpoint);
      if (metadata && metadata.params) {
        const searchParams = new URLSearchParams(url.search);
        Object.keys(metadata.params).forEach(key => {
          searchParams.set(key, metadata.params[key]);
        });
        url.search = searchParams.toString();
      }

      const request = new XMLHttpRequest();
      request.open('POST', url.toString());
      request.setRequestHeader('Authorization', `Bearer ${token}`);

      // Should call the progress method to update the progress to 100% before calling load
      // Setting computable to false switches the loading indicator to infinite mode
      request.upload.onprogress = e => {
        progress(e.lengthComputable, e.loaded, e.total);
      };

      // Should call the load method when done and pass the returned server file id
      // this server file id is then used later on when reverting or restoring a file
      // so your server knows which file to return without exposing that info to the client
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          load(request.responseText);
        } else if (request.status === 401) {
          if (retryOnUnauthorized) {
            AuthService.signinSilent().then(user => {
              token = user.access_token;
              doProcess(fieldName, file, metadata, load, error, progress, abort, false);
            });
          } else {
            error({ response: { status: 401 }, code: 401 }); // translated error will be shown later down the chain by setting these props
          }
        } else if (request.responseText) {
          // Can call the error method if something is wrong, should exit after
          const errorResponse = JSON.parse(request.responseText);
          if (errorResponse.code) {
            error(errorResponse);
          } else {
            error(request.responseText);
          }
        } else {
          error('Error');
        }
      };

      request.send(formData);

      // Should expose an abort method so the request can be cancelled
      return {
        abort: () => {
          // This function is entered if the user has tapped the cancel button
          request.abort();

          // Let FilePond know the request has been cancelled
          abort();
        },
      };
    };

    return {
      process: (fieldName, file, metadata, load, error, progress, abort) =>
        doProcess(fieldName, file, metadata, load, error, progress, abort, true),
    };
  }

  onProcessFile(error, file) {
    const { onFileUploaded, removeSuccessfullAfter } = this.props;
    const fileId = file.id;
    const { pond } = this;
    if (file && file.serverId) {
      const [document] = JSON.parse(file.serverId); // serverId contains response from document service,
      onFileUploaded(error, document, file.getMetadata());
      if (removeSuccessfullAfter) {
        setTimeout(() => {
          if (pond) pond.removeFile(fileId);
        }, removeSuccessfullAfter);
      }
    } else {
      onFileUploaded(error, null, null);
    }
  }

  onAddFile(error, file) {
    const { onFileAdded } = this.props;
    onFileAdded(error, file);
  }

  render() {
    const { pondRef } = this.props;

    return (
      <div className="dropzone dropable-files">
        {/* Pass FilePond properties as attributes */}
        <TranslatedFilePond
          allowMultiple
          server={this.getServerOptions()}
          allowRevert={false}
          allowDrop
          onprocessfile={(error, file) => this.onProcessFile(error, file)}
          onaddfile={(error, file) => {
            this.onAddFile(error, file);
          }}
          pondRef={ref => {
            this.pond = ref;
            pondRef(ref);
          }}
        />
      </div>
    );
  }

  static propTypes = {
    endpoint: PropTypes.string.isRequired,
    onFileAdded: PropTypes.func,
    onFileUploaded: PropTypes.func,
    removeSuccessfullAfter: PropTypes.number,
    pondRef: PropTypes.func,
    relationId: PropTypes.string,
  };

  static defaultProps = {
    onFileAdded: () => {},
    onFileUploaded: () => {},
    pondRef: () => {},
    removeSuccessfullAfter: 0,
    relationId: undefined,
  };
}

export default FileUpload;
